'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { KBareButton, KButton, KInput } from '@scriptera/ui/kit';
import { Link, useRouter } from '@/i18n/routing';
import { isNativeApp, nativeBridge, nativeMethod, useNativeApp } from '@/lib/native-app';
import { BrandBolt } from '@/components/brand-bolt';
import { LocaleSwitch } from '@/components/locale-switch';
import { ThemeToggle } from '@/components/theme';
import './login.css';

// password — служебный шаг ревью-учёток сторов и платформ (apple@/google@/facebook@): пароль вместо кода.
// tfa — второй шаг входа: код принят, но сессию даём только после «Это я» в Telegram.
type Step = 'email' | 'code' | 'password' | 'tfa';

/** Тема, ВЫБРАННАЯ человеком (запись localStorage 'scriptera-theme'), либо null, если
 *  выбора не было. Нативному приветствию (helloAuthState) отдаём только её: атрибут
 *  data-theme без выбора равен системной, и натив принял бы её за выбор. */
function storedTheme(): 'light' | 'dark' | null {
  try {
    const t = localStorage.getItem('scriptera-theme');
    return t === 'dark' || t === 'light' ? t : null;
  } catch {
    return null;
  }
}

interface LoginOk {
  ok: boolean;
  error?: string;
  phone?: boolean;
  isAdmin?: boolean;
  tfa?: { token: string; tgName: string | null };
  /** Вход отменил удаление учётной записи (ТЗ П127.1) — первый экран покажет плашку. */
  restored?: boolean;
}

/** Часовой пояс устройства (IANA) — уезжает на сервер вместе с входом и регистрацией
 *  (владелец 2026-08-21): по нему считаются тихие часы клиента. Отдельной ручки «сообщить
 *  пояс» нет; ручную правку пояса в кабинете этот автосинк не перебивает (tz_source). */
function deviceTz(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/** Фокус БЕЗ прокрутки страницы (`preventScroll`): браузер иначе «показывает» поле —
 *  прокручивает документ к нему, и шапка с логотипом уходит за верхний край. */
function softFocus(el: HTMLInputElement | null) {
  el?.focus({ preventScroll: true });
}

export function LoginForm({
  mode = 'login',
  inSheet = false,
  onSwitchMode,
  onNext,
}: {
  mode?: 'login' | 'register';
  /** ВХОД ШТОРКОЙ В БРАУЗЕРЕ (ТЗ WEB-LOOK Э1.2): форма живёт в листе хоста
   *  (auth/auth-host.tsx) поверх подложки — бренд-ряды и щёлкалки языка/темы не рисуются
   *  (они на подложке), колонка идёт от верха листа. Ветка приложения этим не задета. */
  inSheet?: boolean;
  /** В листе «Уже есть аккаунт» / «Ещё нет аккаунта» не навигируют: хост подменяет тело
   *  на месте и меняет адрес /login ↔ /register (как в iOS — мгновенно, без сети). */
  onSwitchMode?: (m: 'login' | 'register') => void;
  /** В листе после входа без телефона: хост открывает уровень «телефон» поверх формы. */
  onNext?: (step: 'phone') => void;
}) {
  const t = useTranslations('auth');
  const locale = useLocale();
  const router = useRouter();
  const native = useNativeApp();
  // Экран без сессии: натив запоминает «не вошли» и на следующем холодном старте
  // показывает нативное приветствие (владелец 26.08; старые сборки метода не имеют).
  // В приложении с нативным приветствием страница входа живёт только в шторке —
  // хром прячется самим вебом (класс от натива стирался перезагрузкой, 28.08).
  useEffect(() => {
    if (isNativeApp() && nativeMethod('helloState')) {
      document.documentElement.classList.add('in-sheet');
    }
  }, []);

  useEffect(() => {
    if (!isNativeApp()) return;
    // ТЕМА ЧЕСТНАЯ (владелец 26.08 ночь): нативу отдаём тему, ТОЛЬКО если человек выбрал
    // её сам — то есть если она записана в localStorage 'scriptera-theme'. Атрибут
    // data-theme брать нельзя: без выбора там стоит вычисленная системная, и натив
    // запоминал бы её как выбор, а потом открывал светлое на тёмном айфоне. Поля нет —
    // натив стирает сохранённое и следует системе.
    const chosen = storedTheme();
    nativeBridge()
      ?.helloAuthState?.({ in: false, ...(chosen ? { theme: chosen } : {}), lang: locale })
      ?.catch(() => {});
    // Выбор с нативного приветствия, пока в шторке открыт ВХОД (владелец 27.08:
    // «какие кнопки ни нажимай — остаётся войти»): «Мини-тур» и «Зарегистрироваться»
    // уводят на страницу регистрации; какой именно экран показать, она читает из
    // сессионной закладки (событие пропадёт вместе с этой страницей).
    let sub: { remove?: () => void } | undefined;
    // Регистрация — заранее в кэш роутера: «Мини-тур»/«Зарегистрироваться» с героя
    // обязаны менять экран сразу, без видимой паузы (владелец 28.08).
    router.prefetch('/register');
    Promise.resolve(
      nativeBridge()?.addListener?.('helloChoice', (d: { key?: string }) => {
        const k = d.key;
        if (k !== 'examples' && k !== 'skip') return;
        try {
          sessionStorage.setItem('scrHelloGo', k);
        } catch {
          /* нет sessionStorage — просто уйдём на регистрацию, выбор потеряется */
        }
        router.replace('/register');
      }),
    )
      .then((h) => {
        sub = h as { remove?: () => void } | undefined;
      })
      .catch(() => {});
    return () => {
      sub?.remove?.();
    };
  }, [locale, router]);
  const isRegister = mode === 'register';

  // Возврат после входа (?next=…, ставит OAuth-согласие). Только внутренний путь —
  // внешний URL был бы open-redirect'ом. Дубль серверной проверки в login/page.tsx.
  // next уже содержит локаль, поэтому навигация — window.location, не intl-роутер
  // (тот добавил бы локаль второй раз).
  const params = useSearchParams();
  const rawNext = params.get('next');
  const next = rawNext && rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : null;
  // ПЛАШКА ПОСЛЕ ПРОСЬБЫ УДАЛИТЬ УЧЁТНУЮ ЗАПИСЬ (ТЗ П127.1): кабинет увёл сюда с днём
  // закрытия в адресе — говорим, до какого дня вход её возвращает. Мусор в параметре
  // плашки не рисует.
  const deletedAt = new Date(params.get('deleted') ?? '');
  const deletedWords = Number.isFinite(deletedAt.getTime())
    ? deletedAt.toLocaleDateString(locale === 'en' ? 'en-GB' : 'ru-RU')
    : null;
  // Вход отменил удаление — плашку показывает первый экран кабинета, а не эта форма;
  // помним факт до самой навигации (второй шаг в Telegram отвечает уже без него).
  const restoredRef = useRef(false);

  const [step, setStep] = useState<Step>('email');
  const [name, setName] = useState('');
  // Промокод партнёрки (PRICING-PLANS-PLAN этап 7): необязательное поле при регистрации;
  // введённый код сильнее cookie реф-ссылки (решает /api/auth/verify).
  const [promo, setPromo] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** Ошибка формы живёт НА МЕСТЕ ОПИСАНИЯ под заголовком (владелец 28.08): описание
   *  временно подменяется красной строкой и возвращается само — форма не дёргается,
   *  текст перед глазами. Ничего над кнопкой не разворачивается. */
  const sub = (text: ReactNode, ok?: ReactNode | null) => (
    <p className={`auth-sub${error ? ' err' : ok ? ' ok' : ''}`} aria-live="polite">
      {error ?? ok ?? text}
    </p>
  );
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState(false);
  useEffect(() => {
    if (!error) return;
    // Ошибка чувствуется рукой (владелец 28.08) — в приложении дёргаем вибро моста.
    if (isNativeApp()) nativeBridge()?.haptic?.({ kind: 'warning' })?.catch(() => {});
    // В браузере на телефоне — вибрация устройства, где она есть (правило «все ошибки с
    // вибрацией и подсветкой поля»; ТЗ WEB-PARITY-3 В20).
    else if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') navigator.vibrate(30);
    // Строка ошибки СТОИТ, пока человек не начнёт править код (кадр iOS A12-login-code-wrong;
    // прежние 4 с гасили её раньше, чем человек успевал прочитать).
  }, [error]);
  useEffect(() => {
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- гасим ошибку только по правке кода
  }, [code]);
  useEffect(() => {
    if (!resent) return;
    const id = setTimeout(() => setResent(false), 4000);
    return () => clearTimeout(id);
  }, [resent]);
  const [notRegistered, setNotRegistered] = useState(false);
  const [tfa, setTfa] = useState<{ token: string; tgName: string | null } | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const passRef = useRef<HTMLInputElement>(null);

  // Фокус первого поля ставит ЭФФЕКТ, а не атрибут autoFocus (О2, скачки при фокусе):
  // autoFocus заставляет браузер «показать» поле ещё до первого кадра — страница
  // прокручивается к нему, и шапка с логотипом уезжает за экран (в приложении её потом
  // гасил blur, но прокрутка уже случилась). softFocus ставит фокус без прокрутки; на
  // сенсорном экране первого фокуса нет вовсе — там он только дёргает вьюпорт.
  useEffect(() => {
    if (native) {
      (document.activeElement as HTMLElement | null)?.blur?.();
      return;
    }
    if (window.matchMedia('(pointer: coarse)').matches) return;
    softFocus(isRegister ? nameRef.current : emailRef.current);
  }, [native, isRegister]);

  // Шаг пароля (ревью-учётка стора) — тот же фокус без прокрутки, что и на первом шаге.
  useEffect(() => {
    if (step === 'password') softFocus(passRef.current);
  }, [step]);

  const KNOWN_ERRORS = [
    'invalid_email',
    'too_many_requests',
    'invalid_input',
    'invalid_code',
    'not_registered',
    'account_deleted',
    'tfa_rejected',
    'tfa_expired',
  ];
  function errText(key: string | undefined): string {
    return key && KNOWN_ERRORS.includes(key) ? t(`errors.${key}`) : t('errors.generic');
  }

  async function requestCode(): Promise<{ ok: boolean; password?: boolean }> {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/request-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, locale, register: isRegister }),
      });
      const data = (await res.json()) as { ok: boolean; error?: string; password?: boolean };
      if (!data.ok) {
        setNotRegistered(data.error === 'not_registered');
        setError(errText(data.error));
        return { ok: false };
      }
      return { ok: true, password: data.password };
    } catch {
      setError(errText(undefined));
      return { ok: false };
    } finally {
      setBusy(false);
    }
  }

  async function onEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    const r = await requestCode();
    if (r.ok) {
      // Ревью-учётка стора → шаг пароля, письмо с кодом не уходило.
      setStep(r.password ? 'password' : 'code');
      if (!r.password) setTimeout(() => softFocus(codeRef.current), 60);
    }
  }

  async function verify(codeValue: string, asPassword = false) {
    if (busy) return;
    setBusy(true);
    setError(null);
    // При успехе кнопку НЕ разблокируем: до фактического перехода она остаётся
    // «Проверяем…» — иначе мигает активной, пока идёт навигация.
    let success = false;
    try {
      const res = await fetch('/api/auth/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          asPassword
            ? { email, password: codeValue, tz: deviceTz() } // ревью-учётка стора
            : {
                email,
                code: codeValue,
                name: isRegister ? name : undefined,
                promo: isRegister && promo.trim() ? promo.trim() : undefined,
                register: isRegister,
                locale,
                tz: deviceTz(),
              },
        ),
      });
      const data = (await res.json()) as LoginOk;
      if (!data.ok) {
        setError(errText(data.error));
        return;
      }
      if (data.restored) restoredRef.current = true;
      // Второй шаг включён: сессии ещё нет — уходим на экран ожидания Telegram.
      if (data.tfa) {
        success = true; // кнопку не разблокируем: экран сменится
        setTfa(data.tfa);
        setStep('tfa');
        return;
      }
      success = goAfterLogin(data);
    } catch {
      setError(errText(undefined));
    } finally {
      if (!success) setBusy(false);
    }
  }

  /** Куда вести после выдачи сессии (общее для кода, пароля и подтверждения в Telegram).
   *  true = навигация запущена, кнопку разблокировать не нужно. */
  function goAfterLogin(data: LoginOk): boolean {
    if (data.restored) restoredRef.current = true;
    // Телефон подтверждён (или админ) и есть next → сразу туда (OAuth-согласие;
    // оплата не нужна: ассистент без сервера работает в справочном режиме).
    if (next && (data.isAdmin || data.phone)) {
      window.location.assign(next);
      return true;
    }
    // Админ — сразу в кабинет (без телефона/оплаты). Иначе: телефон → кабинет,
    // чей гейт сам ветвит неоплативших по режиму регистрации (пейволл или free).
    const to = data.isAdmin ? '/app' : !data.phone ? '/verify-phone' : '/app';
    // Лист входа (Э1.2): телефон — следующим листом стопки, а не переходом на страницу.
    if (inSheet && to === '/verify-phone' && onNext) {
      onNext('phone');
      return true;
    }
    // Вход вернул учётную запись из удаления (ТЗ П127.1) — просим первый экран сказать
    // об этом плашкой; метка одноразовая, кабинет стирает её из адреса.
    router.replace(restoredRef.current ? `${to}?restored=1` : to);
    router.refresh();
    return true;
  }

  // Ожидание ответа из бота: опрос статуса заявки раз в 2 секунды. «Это я» → сессия и
  // переход; «Не я» / истекло → назад на ввод кода с понятной причиной.
  useEffect(() => {
    if (step !== 'tfa' || !tfa) return;
    let alive = true;
    const tick = async () => {
      try {
        const res = await fetch('/api/auth/confirm', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: tfa.token, tz: deviceTz() }),
        });
        const data = (await res.json()) as LoginOk & { status?: string };
        if (!alive) return;
        if (data.status === 'confirmed') {
          clearInterval(timer);
          goAfterLogin(data);
          return;
        }
        if (data.status === 'rejected' || data.status === 'expired') {
          clearInterval(timer);
          setTfa(null);
          setStep('code');
          setCode('');
          setBusy(false);
          setError(errText(data.status === 'rejected' ? 'tfa_rejected' : 'tfa_expired'));
        }
      } catch {
        /* сеть мигнула — ждём следующего тика */
      }
    };
    const timer = setInterval(() => void tick(), 2000);
    void tick();
    return () => {
      alive = false;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, tfa]);

  function onCodeSubmit(e: React.FormEvent) {
    e.preventDefault();
    void verify(code);
  }

  async function resend() {
    setResent(false);
    if ((await requestCode()).ok) setResent(true);
  }

  return (
    <div className={`auth-card${inSheet ? ' in-sheet-form' : ''}`}>
      {inSheet ? null : (
        <>
      {/* Переключатель языка справа сверху — и в приложении, и на вебе (владелец 2026-07-20):
          на странице входа отдельного лендинга/шапки может не быть, язык меняем прямо тут. */}
      {/* Язык и тема — те же кнопки-«щёлкалки», что на лендинге и интро (владелец
          26.08: «везде»), справа сверху в одной строке с бренд-рядом. */}
      <div className="auth-ctl2">
        <LocaleSwitch compact className="auth-tctl" />
        <ThemeToggle compact className="auth-tctl" />
      </div>
      {/* Бренд-ряд двух видов, показ решает CSS (владелец 26.08 «кругом всё ровно»):
          на мобильных (браузер И приложение) — угловой ряд со знаком 34 в точке
          посадки нативного приветствия (20+safe / 14+safe); на десктопе — карточный
          ряд со знаком 44. Рендерим оба: клиентский детект давал бы прыжок гидрации. */}
      <div className="auth-corner">
        <BrandBolt size={34} />
        <span className="auth-name">Scriptera</span>
      </div>
      <div className="auth-brand">
        <BrandBolt size={44} />
        <span className="auth-name">Scriptera</span>
      </div>
        </>
      )}

      <div className="auth-scroll">
      {step === 'email' ? (
        <form onSubmit={onEmailSubmit} noValidate>
          <h1 className="auth-h">{isRegister ? t('registerTitle') : t('title')}</h1>
          {/* Пришли из кабинета после «Удалить учётную запись» — описание подменяется
              словами про срок возврата (ТЗ П127.1). */}
          {sub(
            deletedWords && !isRegister
              ? t('deletedNotice', { date: deletedWords })
              : isRegister
                ? t('registerSubtitle')
                : t('subtitle'),
          )}
          {isRegister && (
            <>
              <label className="auth-lbl" htmlFor="name">
                {t('nameLabel')}
              </label>
              <KInput
                id="name"
                ref={nameRef}
                type="text"
                autoComplete="name"
                maxLength={80}
                className="auth-input"
                placeholder={t('namePlaceholder')}
                value={name}
                onChange={(ev) => setName(ev.target.value)}
              />
            </>
          )}
          <label className="auth-lbl" htmlFor="email">
            {t('emailLabel')}
          </label>
          <KInput
            id="email"
            ref={emailRef}
            type="email"
            inputMode="email"
            autoComplete="email"
            className="auth-input"
            placeholder={t('emailPlaceholder')}
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
          />
          {isRegister && (
            <>
              <label className="auth-lbl" htmlFor="promo">
                {t('promoLabel')}
              </label>
              <KInput
                id="promo"
                type="text"
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                maxLength={32}
                className="auth-input"
                placeholder={t('promoPlaceholder')}
                value={promo}
                onChange={(ev) => setPromo(ev.target.value)}
              />
            </>
          )}
          {/* Регистрация доступна и в приложении (владелец 26.08.2026, отменяет прежнее
              «в приложении только вход»): приложение без сессии открывается сразу на
              регистрации, вход — кнопкой «Уже есть аккаунт» ниже. Цен и оплат на этих
              экранах нет — правила сторов не задеты. */}
          {notRegistered && !isRegister && (
            inSheet && onSwitchMode ? (
              <KBareButton className="auth-link auth-link-block" onClick={() => onSwitchMode('register')}>
                {t('toRegister')} →
              </KBareButton>
            ) : (
              <Link className="auth-link auth-link-block" href="/register">
                {t('toRegister')} →
              </Link>
            )
          )}
          <KButton
            type="submit"
            tone="primary"
            block
            className="auth-btn"
            disabled={busy || !email || (isRegister && !name.trim())}
          >
            {busy ? t('sending') : t('getCode')}
          </KButton>
          {/* Регистрация — главный путь нового человека; вход всегда под рукой
              кнопкой «Уже есть аккаунт» (владелец 26.08.2026). */}
          {isRegister ? (
            <div className="auth-links" style={{ justifyContent: 'center' }}>
              {inSheet && onSwitchMode ? (
                <KBareButton className="auth-link" onClick={() => onSwitchMode('login')}>
                  {t('haveAccount')}
                </KBareButton>
              ) : (
                <Link className="auth-link" href="/login">
                  {t('haveAccount')}
                </Link>
              )}
            </div>
          ) : (
            /* Зеркальная постоянная ссылка на входе (владелец 26.08): ещё нет
               аккаунта — на регистрацию. */
            <div className="auth-links" style={{ justifyContent: 'center' }}>
              {inSheet && onSwitchMode ? (
                <KBareButton className="auth-link" onClick={() => onSwitchMode('register')}>
                  {t('noAccount')}
                </KBareButton>
              ) : (
                <Link className="auth-link" href="/register">
                  {t('noAccount')}
                </Link>
              )}
            </div>
          )}
        </form>
      ) : step === 'tfa' ? (
        <div>
          <h1 className="auth-h">{t('tfaTitle')}</h1>
          {sub(tfa?.tgName ? t('tfaSubtitle', { acc: tfa.tgName }) : t('tfaSubtitleNoAcc'))}
          <div className="auth-ok">{t('tfaWaiting')}</div>
          <div className="auth-links">
            <KBareButton
              className="auth-link"
              onClick={() => {
                setTfa(null);
                setStep('email');
                setCode('');
                setBusy(false);
                setError(null);
              }}
            >
              {t('changeEmail')}
            </KBareButton>
          </div>
        </div>
      ) : step === 'password' ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void verify(password, true);
          }}
          noValidate
        >
          <h1 className="auth-h">{t('passwordTitle')}</h1>
          {sub(t('passwordSubtitle', { email }))}
          <label className="auth-lbl" htmlFor="password">
            {t('passwordLabel')}
          </label>
          <KInput
            id="password"
            ref={passRef}
            type="password"
            autoComplete="current-password"
            className="auth-input"
            value={password}
            onChange={(ev) => setPassword(ev.target.value)}
          />
          <KButton type="submit" tone="primary" block className="auth-btn" disabled={busy || !password}>
            {busy ? t('verifying') : t('signIn')}
          </KButton>
          <div className="auth-links">
            <KBareButton
              className="auth-link"
              onClick={() => {
                setStep('email');
                setPassword('');
                setError(null);
              }}
            >
              {t('changeEmail')}
            </KBareButton>
          </div>
        </form>
      ) : (
        <form onSubmit={onCodeSubmit} noValidate>
          <h1 className="auth-h">{t('codeTitle')}</h1>
          {sub(isRegister ? t('codeSubtitleReg', { email }) : t('codeSubtitle', { email }), resent ? t('resent') : null)}
          <label className="auth-lbl" htmlFor="code">
            {t('codeLabel')}
          </label>
          {/* ПЯТЬ КВАДРАТОВ (ТЗ WEB-PARITY-3 В13, кадр iOS A07-register-code): цифры стоят
              по одной в квадратах, активный — розовой обводкой; само поле прозрачное над
              ними, набор и автозаполнение кода — как у обычного поля. Код набран целиком —
              активной ячейки нет (updateCodeBoxes: digits.count < 5; кадр A12-login-code-wrong). */}
          <div className="auth-code-wrap" onClick={() => codeRef.current?.focus()}>
            <div className="auth-code-boxes" aria-hidden>
              {[0, 1, 2, 3, 4].map((i) => (
                <span key={i} className={`auth-code-box${i === code.length ? ' is-on' : ''}${error ? ' is-bad' : ''}`}>
                  {code[i] ?? ''}
                </span>
              ))}
            </div>
            <KInput
              id="code"
              ref={codeRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={5}
              className="auth-input auth-code"
              aria-label={t('codeLabel')}
              value={code}
              onChange={(ev) => {
                const v = ev.target.value.replace(/\D/g, '').slice(0, 5);
                setCode(v);
                // Авто-вход: все 5 цифр введены — не заставляем жать кнопку.
                if (v.length === 5) void verify(v);
              }}
            />
          </div>
          <KButton type="submit" tone="primary" block className="auth-btn" disabled={busy || code.length !== 5}>
            {busy ? t('verifying') : isRegister ? t('continueReg') : t('signIn')}
          </KButton>
          <div className="auth-links">
            <KBareButton
              className="auth-link"
              onClick={() => {
                setStep('email');
                setCode('');
                setError(null);
                setResent(false);
              }}
            >
              {t('changeEmail')}
            </KBareButton>
            <KBareButton className="auth-link" onClick={resend} disabled={busy}>
              {t('resend')}
            </KBareButton>
          </div>
        </form>
      )}
      </div>
    </div>
  );
}
